import React from 'react';
import PropTypes from 'prop-types';

import Styles from './onboarding.module.css';

import EventTypesModel from '../../models/subtypeEventModel';
import OrganizationModel from '../../models/organization';

import { useForm, requiredField } from '../../utils/validators';
import Loading from '../../components/loading';
import { ReactComponent as WeezLogo } from '../../images/weez-logo-C-blue01.svg';
import { useUser } from '../../simple-connect';
import { ToastContext, RadioLabel, Button, Select, Tooltip, Checkbox, ViewPort } from '@weezevent/nacre';
import useMixpanel from '../../context/useMixpanel';

import { useTranslation, Trans } from 'react-i18next';
import i18n from 'i18next';
import useAppContext from '../../context/useAppContext';

import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import OrganizationInfos from '../../models/organizationInfos';
import TopBarModules from '../../components/template/topBarModules/topBarModules';
import TicketLogo from '../../nacre/images/weezticket.svg';
import { fetcher } from '@weezevent/weezjs-core';

import Cookies from 'js-cookie';

function OnboardingHeader({ organization }) {
    const { user } = useUser();
    return (
        <div className={Styles['onboarding-header-container']}>
            <div className={Styles['onboarding-header-logo']}>
                <WeezLogo />
            </div>
            <div className={Styles['onboarding-header-description']}>
                {<Trans i18nKey={`ticket.onboarding.description.${organization.onboarding_date ? 'old_orga' : 'new_orga'}`} values={{ first_name: user.firstname }} />}
            </div>
        </div>
    );
}

OnboardingHeader.propTypes = {
    organization: PropTypes.object.isRequired
};

function EventOnboardingPicker({ value, types, onChange, placeholder }) {
    const items = React.useMemo(() => {
        if (!types) {
            return [];
        }
        return types.map(type => ({
            key: type.pk,
            text: type.label,
            value: type.pk
        }));
    }, [types]);

    return (
        <div className={Styles['event-types-picker-container']}>
            <div className={Styles['description']}>{placeholder}</div>
            <div className={Styles['event-types-select']}>
                <Select value={value} options={items} placeholder={placeholder} onChange={onChange} />
            </div>
            <div className={Styles['event-types-picker']}>
                {types.map(type => (
                    <RadioLabel
                        inline
                        bordered
                        key={type.pk}
                        name={type.pk}
                        label={type.label}
                        active={value === type.pk}
                        checked={value === type.pk}
                        className={`${Styles['radio-label-custom']}`}
                        onClick={() => onChange(type.pk)}
                    />
                ))}
            </div>
        </div>
    );
}

EventOnboardingPicker.propTypes = {
    value: PropTypes.number,
    types: PropTypes.array.isRequired,
    onChange: PropTypes.func
};

function PaidOrFreePicker({ mostlyPaid, onChange }) {
    const { t } = useTranslation();
    return (
        <div className={Styles['paid-or-free-picker-container']}>
            <div className={Styles['description']}>{t('ticket.onboarding.type.description')}</div>
            <div className={Styles['paid-or-free-picker']}>
                <RadioLabel
                    inline
                    bordered
                    name="free"
                    label={t('ticket.onboarding.type.free')}
                    active={mostlyPaid === false}
                    checked={mostlyPaid === false}
                    onClick={() => onChange(false)}
                    className={`${Styles['radio-label-custom']}`}
                />
                <RadioLabel
                    inline
                    bordered
                    name="pay"
                    label={t('ticket.onboarding.type.paying')}
                    active={mostlyPaid === true}
                    checked={mostlyPaid === true}
                    onClick={() => onChange(true)}
                    className={`${Styles['radio-label-custom']}`}
                />
            </div>
        </div>
    );
}

PaidOrFreePicker.propTypes = {
    mostlyPaid: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

function TermsAndConditions({ terms_and_conditions, onChange }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const prefix = 'https://weezevent.com/';

    const termsUrl = React.useMemo(() => {
        const validLanguages = ['es-es', 'fr-fr', 'fr-ca', 'fr-ch', 'en-ca'];
        let locale = i18n.accountLocale.toLowerCase();
        locale = validLanguages.includes(locale) ? locale : 'en-gb';
        const lan = locale.split('-');
        locale = lan[0] === lan[1] ? lan[0] : locale;
        return `${prefix}${locale}/${lan[0] === 'en' ? 'terms-of-use' : 'cgus'}/`;
    }, []);

    if (organization.terms_and_conditions) {
        return null;
    }

    return (
        <div className={Styles['terms-and-conditions-container']}>
            <Checkbox name="cgus" active={terms_and_conditions} checked={terms_and_conditions} onClick={() => onChange(!terms_and_conditions ? true : null)} />
            <div className={Styles['description']}>
                {t('ticket.onboarding.terms_and_conditions.accept')}
                <a href={termsUrl} target="_blank" rel="noreferrer noopener">
                    {t('ticket.onboarding.terms_and_conditions.link')}
                </a>
            </div>
        </div>
    );
}

TermsAndConditions.propTypes = {
    terms_and_conditions: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired
};

function SubmitButtonTooltip({ displayTooltip, children }) {
    const { t } = useTranslation();
    if (!displayTooltip) {
        return children;
    }
    return (
        <Tooltip visible={true} triggerOnHover={true} title={t('ticket.onboarding.submit_tooltip')}>
            {children}
        </Tooltip>
    );
}
SubmitButtonTooltip.propTypes = {
    displayTooltip: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired
};

export default function Onboarding({ organization }) {
    const [types, loading, sync] = EventTypesModel.useApiModel({ organization_id: organization.pk });
    const { t } = useTranslation();
    const mixpanel = useMixpanel();
    const toast = React.useContext(ToastContext);
    const [organizationInfos, loadingOrgaInfos] = OrganizationInfos.useApiModel({
        organizationId: organization.id
    });

    const history = useHistory();

    const [form, setForm, setObject, , formValidate] = useForm(
        {
            events_sector: null,
            mostly_paid: null,
            attendees_per_year: null,
            terms_and_conditions: null
        },
        {
            events_sector: requiredField,
            mostly_paid: requiredField,
            attendees_per_year: requiredField,
            terms_and_conditions: requiredField
        }
    );

    const expectedAttendees = [
        {
            pk: 1,
            label: t('ticket.onboarding.attendees.minus_500')
        },
        {
            pk: 2,
            label: t('ticket.onboarding.attendees.until_2500')
        },
        {
            pk: 3,
            label: t('ticket.onboarding.attendees.until_5000')
        },
        {
            pk: 4,
            label: t('ticket.onboarding.attendees.more_than_5000')
        }
    ];

    const handleSubmit = React.useCallback(() => {
        const { terms_and_conditions: _, ...settings_prospection } = form;
        let { terms_and_conditions } = form;
        if (terms_and_conditions && typeof terms_and_conditions == 'boolean') {
            terms_and_conditions = new Date().toISOString();
        }
        const updated_orga = new OrganizationModel({ id: organization.pk, terms_and_conditions, settings_prospection });
        updated_orga
            .save({})
            .then(() => {
                const label = expectedAttendees.find(attendee => attendee.pk === form.attendees_per_year).label;
                const mixpanelData = {
                    'Organizer’s Sector': types.find(type => type.pk === form.events_sector).type,
                    'Organizer’s Sector ID': form.events_sector,
                    'Organizer’s average attendees by year': label,
                    'Organizer mainly paid': form.mostly_paid,
                    'Organizer has accepted T&C': form.terms_and_conditions
                };
                mixpanel.track('ACTION - Onboarding : Answer prospection', mixpanelData);
                mixpanel.people.set(mixpanelData);
                toast.success(t('ticket.onboarding.save.success'));
                let pathRedirect = !organizationInfos.has_event ? `/ticket/O${organization.id}/events/new` : `/ticket/O${organization.pk}`;
                history.push(pathRedirect);
            })
            .catch(e => {
                Sentry.captureException(e);
                toast.error(t('ticket.onboarding.save.error'));
            });
    }, [form, organization, types, organizationInfos]);

    const disableSave = React.useMemo(() => {
        if (!formValidate()) {
            return true;
        }
        return form['attendees_per_year'] === 0;
    }, [form]);

    const sortedTypes = React.useMemo(() => {
        if (!types) {
            return [];
        }
        return types.sort((type1, type2) => {
            // Need Other type (id 39) at the end of the list
            if (type1.pk === 39) {
                return 1;
            }
            if (type2.pk === 39) {
                return -1;
            }
            return type1.label.localeCompare(type2.label);
        });
    }, [types]);

    React.useEffect(() => {
        if (!organization?.settings_prospection) {
            setForm('terms_and_conditions', organization.terms_and_conditions || null);
        } else {
            const { attendees_per_year, mostly_paid, events_sector } = organization.settings_prospection;
            const terms_and_conditions = organization.terms_and_conditions || null;
            setObject({
                events_sector,
                mostly_paid,
                attendees_per_year,
                terms_and_conditions
            });
        }
    }, [organization]);

    React.useEffect(() => {
        async function fetchData() {
            const settingsProspectionKeys = organization?.settings_prospection ? Object.keys(organization?.settings_prospection) : [];
            const origin_levels = ['origin_level1', 'origin_level2', 'origin_level3'];
            let trackingData = [];

            if (!settingsProspectionKeys.includes(origin_levels)) {
                if (Cookies.get('weezorigin')) {
                    let decodeURI = decodeURIComponent(Cookies.get('weezorigin'));
                    trackingData = atob(decodeURI);
                    return await fetcher.post(`/ticket/organizations/${organization.id}/settings/tracking_data`, { data: trackingData });
                }
            }
        }
        fetchData();
    }, []); // one time only

    React.useEffect(() => {
        // We use setTimeout here to delay the execution of the sync function.
        // This is necessary because we need to ensure that the fetcher has completed
        // changing the language before we call the sync function.
        setTimeout(() => {
            sync();
        }, 0);
    }, [t]);

    if (loading || loadingOrgaInfos || !organization) {
        return <Loading />;
    }

    return (
        <ViewPort
            organization={organization}
            logo={TicketLogo}
            onClickLogo={organization && organizationInfos.has_event ? () => history.push(`/ticket/O${organization.pk}`) : null}
            topBarModules={<TopBarModules displaySwitchLang={true} minimal={true} organization={organization} deleteAccount={true} />}
        >
            <div className={Styles['container']}>
                <div className={Styles['onboarding-container']}>
                    <OnboardingHeader organization={organization} />
                    <EventOnboardingPicker
                        value={form.events_sector}
                        types={sortedTypes}
                        onChange={events_sector => setForm('events_sector', events_sector)}
                        placeholder={t('ticket.onboarding.sector.description')}
                    />
                    <EventOnboardingPicker
                        value={form.attendees_per_year}
                        types={expectedAttendees}
                        onChange={attendees_per_year => setForm('attendees_per_year', attendees_per_year)}
                        placeholder={t('ticket.onboarding.attendees.description')}
                    />
                    <PaidOrFreePicker mostlyPaid={form.mostly_paid} onChange={mostlyPaid => setForm('mostly_paid', mostlyPaid)} />
                    <TermsAndConditions terms_and_conditions={form.terms_and_conditions} onChange={terms_and_conditions => setForm('terms_and_conditions', terms_and_conditions)} />
                    <SubmitButtonTooltip displayTooltip={disableSave}>
                        <Button primary disabled={disableSave} onClick={handleSubmit} className={Styles['onboarding-submit']}>
                            {t('ticket.common.action.continue')}
                        </Button>
                    </SubmitButtonTooltip>
                </div>
            </div>
        </ViewPort>
    );
}

Onboarding.propTypes = {
    organization: PropTypes.object.isRequired
};
